import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { id: "supporter", amount: 25 },
  { id: "advocate", amount: 50 },
  { id: "champion", amount: 100 },
  { id: "leader", amount: 250 },
];

export const Donate = () => {
  const [packageId, setPackageId] = useState("advocate");
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ firstName: "", lastName: "", email: "" });
  const [loading, setLoading] = useState(false);

  const selectPreset = (id) => {
    setPackageId(id);
    setCustom("");
  };

  const donate = async () => {
    const firstName = donor.firstName.trim();
    const lastName = donor.lastName.trim();
    const email = donor.email.trim();
    if (!firstName || !lastName) {
      toast.error("Please enter your first and last name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const body = {
      origin_url: window.location.origin,
      name: `${firstName} ${lastName}`,
      email,
    };
    if (custom) {
      const amt = parseFloat(custom);
      if (!amt || amt < 1) {
        toast.error("Please enter a donation amount of at least $1.");
        return;
      }
      body.custom_amount = amt;
    } else {
      body.package_id = packageId;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/donations/checkout`, body);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="py-20 lg:py-32 bg-white" data-testid="donate-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-600">Support The Campaign</p>
          <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight">
            Fuel a fresh voice for our schools
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md">
            Every contribution helps reach more families across Pembina Trails with a
            positive, student-focused message. Your support makes it possible.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck size={18} className="text-teal-600" />
            Secure payment processed by Stripe.
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-7 sm:p-9" data-testid="donate-card">
            <Label className="text-slate-700 font-medium">Choose an amount <span className="text-slate-400 font-normal">(CAD)</span></Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PRESETS.map((p) => {
                const active = !custom && packageId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => selectPreset(p.id)}
                    className={`rounded-xl border px-4 py-5 text-center transition-colors ${
                      active
                        ? "bg-slate-900 border-slate-900 text-white"
                        : "bg-white border-slate-200 text-slate-700 hover:border-teal-400"
                    }`}
                    data-testid={`donate-preset-${p.id}`}
                  >
                    <div className="font-display text-2xl font-extrabold">${p.amount}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4">
              <Label htmlFor="custom" className="text-slate-700 font-medium">Or enter a custom amount</Label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                <Input
                  id="custom"
                  type="number"
                  min="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Custom amount"
                  className="pl-8 h-12 rounded-xl border-slate-200 focus-visible:ring-teal-500 bg-white"
                  data-testid="donate-custom-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div>
                <Label htmlFor="dfirst" className="text-slate-700 font-medium">First name <span className="text-teal-600">*</span></Label>
                <Input
                  id="dfirst"
                  value={donor.firstName}
                  onChange={(e) => setDonor({ ...donor, firstName: e.target.value })}
                  placeholder="First name"
                  required
                  className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-teal-500 bg-white"
                  data-testid="donate-firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="dlast" className="text-slate-700 font-medium">Last name <span className="text-teal-600">*</span></Label>
                <Input
                  id="dlast"
                  value={donor.lastName}
                  onChange={(e) => setDonor({ ...donor, lastName: e.target.value })}
                  placeholder="Last name"
                  required
                  className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-teal-500 bg-white"
                  data-testid="donate-lastname-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="demail" className="text-slate-700 font-medium">Email <span className="text-teal-600">*</span></Label>
                <Input
                  id="demail"
                  type="email"
                  value={donor.email}
                  onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-teal-500 bg-white"
                  data-testid="donate-email-input"
                />
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={donate}
              disabled={loading}
              className="group mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-teal-600 text-white font-semibold px-7 py-4 hover:bg-teal-700 transition-colors disabled:opacity-70"
              data-testid="donate-submit-button"
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" /> Redirecting to Stripe...</>
              ) : (
                <><Heart size={18} /> Donate Now</>
              )}
            </motion.button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
